<template>
	<div class="flex flex-col">
		<transition name="fade" mode="out-in" appear>
			<div class="overflow-y-auto max-h-full" v-if="slide == 1">
				<div class="bg-capgemini-pink p-4 leading-none">
					<h3 class="text-sm font-bold text-white uppercase tracking-wide mb-1">Securing Business Strategy</h3>
					<h2 class="text-lg font-bold">Phase 1: Security Strategy by Design & Default </h2>
				</div>
				<div class="p-4">
					<h4 class="font-bold text-lg mb-2">From Security Issues &hellip;</h4>
					<ul class="ml-6 mb-4">
						<li class="mb-1 list-disc">If security is not bound up with the process of Digital Transformation (DX) you open your business up to substantial financial and reputational risks.</li>
						<li class="mb-1 list-disc">Security delays reduce flexibility, introduce inefficiency, undermine agility, and impact speed to market.</li>
						<li class="mb-1 list-disc">If security does not keep pace with the modern transforming business, it will hold change and progress back, resulting in digital deadlock.</li>
					</ul>
					<h4 class="font-bold text-lg mb-2">&hellip; to Security Outcomes</h4>
					<ul class="ml-6">
						<li class="mb-1 list-disc">Security enabled at the beginning of the transformation results in higher levels of success. Security by design is considered the key DX element by 57% of organizations. </li>
						<li class="mb-1 list-disc">Innovation processes with security at their core will incubate and scale far more effectively. </li>
						<li class="mb-1 list-disc">Wastage and costs from failed projects are reduced, and impacts on business risks mitigated.</li>
						<li class="mb-1 list-disc">A change in the way security is viewed as a business enabler leads to improved competitive advantage and shareholder value.</li>
					</ul>
				</div>
				<div class="px-2 mb-4">
					<div class="bg-gray-600 text-white p-2 font-bold text-center">
						The digital strategy of your organization must include security as a milestone
					</div>
					<div class="flex">
						<div class="flex-1 p-3 border mr-1">Approach strategy with security as an embedded part of the planning process. It is your responsibility to make this a reality. </div>
						<div class="flex-1 p-3 border mr-1">Treat security as an enabler and involve the security function earlier in the process to reduce risk and increase efficiencies in process.</div>
						<div class="flex-1 p-3 border">Bring the CISO function higher up the decision-making process and embed them in all strategic decision-making activity. </div>
					</div>
				</div>
			</div>
			<div class="overflow-y-auto max-h-full" v-else-if="slide == 2">
				<div class="bg-capgemini-pink p-4 leading-none">
					<h3 class="text-sm font-bold text-white uppercase tracking-wide mb-1">Define a Blueprint for Success</h3>
					<h2 class="text-lg font-bold">Phase 2: Build the Plan for Security Transformation</h2>
				</div>
				<div class="p-4">
					<div class="flex mb-4">
						<div class="w-1/5">
							<img class="w-full" src="../assets/icons/ceo-1.svg" alt="">
						</div>
						<div class="w-4/5">
							<h4 class="font-bold mb-2 text-lg">Security Alignment for DX</h4>
							<p>Define and formalize the role security should play in all processes, functions, and initiatives, such as DX. Demonstrate that business and security are on the same page with the same goals.</p>
						</div>
					</div>
					<div class="flex mb-4">
						<div class="w-1/5">
							<img class="w-full" src="../assets/icons/ceo-2.svg" alt="">
						</div>
						<div class="w-4/5">
							<h4 class="font-bold mb-2 text-lg">Endorse Security Change</h4>
							<p>Formalize and strengthen the role of security in the governance structure. Communicate the enhanced role of security with senior stakeholders. Ensure they are supported in their transformation.</p>
						</div>
					</div>
					<div class="flex mb-4">
						<div class="w-1/5">
							<img class="w-full" src="../assets/icons/ceo-3.svg" alt="">
						</div>
						<div class="w-4/5">
							<h4 class="font-bold mb-2 text-lg">Empower the Security Function</h4>
							<p>Ensure your security function has the right skills and language to hold the conversations needed to change perception and role. Support them by providing a sufficient level of authority.</p>
						</div>
					</div>
					<div class="flex mb-4">
						<div class="w-1/5">
							<img class="w-full" src="../assets/icons/ceo-4.svg" alt="">
						</div>
						<div class="w-4/5">
							<h4 class="font-bold mb-2 text-lg">Formalize Security Change</h4>
							<p>Failures in security are hugely impactful on the business. Instil the need for change, the process of change, and the implications of not going through this — financial and reputational.</p>
						</div>
					</div>
				</div>
			</div>
			<div class="overflow-y-auto max-h-full" v-else-if="slide == 3">
				<div class="bg-capgemini-pink p-4 leading-none">
					<h3 class="text-sm font-bold text-white uppercase tracking-wide mb-1">Evangelize Security as Risk Mitigation</h3>
					<h2 class="text-lg font-bold">Phase 3: Bring Security into Constant Board Discussion</h2>
				</div>
				<div class="p-4">
					<div class="flex flex-wrap">
						<div class="w-1/2">
							<div class="p-2">
								<div class="flex flex-col mb-2 text-center">
									<h4 class="font-bold border border-gray-600 rounded-t px-2 py-1">Risk Mitigation Becomes a Core Function</h4>
									<img class="w-full mx-auto" src="../assets/icons/direction.svg" alt="" style="margin-top: 0">
								</div>
								<p>Communicate the progressive role of security in the overarching business strategy in the context of risk mitigation. </p>
								<p>Highlight and support outcomes and business advantages. </p>
							</div>
						</div>
						<div class="w-1/2">
							<div class="p-2">
								<div class="flex flex-col mb-2 text-center">
									<h4 class="font-bold border border-gray-600 rounded-t px-2 py-1">Change the Measures of Security</h4>
									<img class="w-full mx-auto" src="../assets/icons/direction.svg" alt="" style="margin-top: 0">
								</div>
								<p>Determine business-relevant security and performance metrics and KPIs. Empower all functions to track and report these.</p>
								<p>Shift security KPIs and traditional measures of security to align with those of the wider business. </p>
							</div>
						</div>
						<div class="w-1/2">
							<div class="p-2">
								<div class="flex flex-col mb-2 text-center">
									<h4 class="font-bold border border-gray-600 rounded-t px-2 py-1">Enshrine Security in Business Process</h4>
									<img class="w-full mx-auto" src="../assets/icons/direction.svg" alt="" style="margin-top: 0">
								</div>
								<p>Bring security in early as part of the business strategy and transformative process.</p>
								<p>Be the one that forces security by design and by default into all business activities and processes.</p>
							</div>
						</div>
						<div class="w-1/2">
							<div class="p-2">
								<div class="flex flex-col mb-2 text-center">
									<h4 class="font-bold border border-gray-600 rounded-t px-2 py-1">Define the Future-Fit CISOs<br><br></h4>
									<img class="w-full mx-auto" src="../assets/icons/direction.svg" alt="" style="margin-top: 0">
								</div>
								<p>Ensure you have a CISO in place that can address these issues, speak the business language (or create a function that can for them on a day-to-day basis) and understand mitigating risk, not just tech and back office.</p>
							</div>
						</div>
						<div class="w-full text-center">
							<div class="p-2">
								<div class="flex flex-col mb-2 text-center">
									<h4 class="font-bold border border-gray-600 rounded-t px-2 py-1">DRIVE PROGRESS</h4>
									<img class="w-full mx-auto" src="../assets/icons/direction.svg" alt="" style="margin-top: 0">
								</div>
								<p>Demonstrate progress by empowering wins to develop and maintain momentum and create sticky change. Do not let this issue drop from consciousness.</p>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="overflow-y-auto max-h-full" v-else-if="slide == 4">
				<div class="bg-capgemini-pink p-4 leading-none">
					<h3 class="text-sm font-bold text-white uppercase tracking-wide mb-1">Do Not Rest. Build On Security Change</h3>
					<h2 class="text-lg font-bold">Phase 4: Empower Security to Continue to Evolve</h2>
				</div>
				<div class="pb-16 pt-4 relative">
					<div class="absolute inset-0 bg-gray-200 z-0">
						<img class="h-full mx-auto" src="../assets/icons/arrow-down.svg" alt="">
					</div>
					<div class="px-2 mt-2 relative z-10">
						<div class="bg-gray-600 text-white p-2 font-bold text-center shadow">
							Do Not Rest!
						</div>
						<div class="flex">
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">Prevent your organization from “snapping back” to old habits. Empower change and “unlearn” all ways of working. </div>
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">Ingrain security into the business and promote the development of process and training for all employees, partners, and ecosystem players.</div>
							<div class="flex-1 p-2 border border-t-0 bg-white">Actively drive to improve engagement and functionality of security in the context of risk mitigation and the need for change. </div>
						</div>
					</div>
					<div class="px-2 mt-2 relative z-10">
						<div class="bg-gray-600 text-white p-2 font-bold text-center shadow">
							Embed Security as Business Enabler
						</div>
						<div class="flex">
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">Embed security change in the processes of the business. Think about compliance as an example of how well some things are addressed and whether security meets this standard.</div>
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">Encourage and mandate collaboration. Measure and target departments and individuals on collaborative working and on security involvement.</div>
							<div class="flex-1 p-2 border border-t-0 bg-white">Focus collaboration on speed to market, agility, cost efficiency, and productivity — all balanced against risk — as the future security role.</div>
						</div>
					</div>
					<div class="px-2 mt-2 relative z-10">
						<div class="bg-gray-600 text-white p-2 font-bold text-center shadow">
							Security Supports Wider Ambitions
						</div>
						<div class="flex">
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">Support your security function moving to automation and selective outsourcing, allowing staff to focus on essential business outcomes.</div>
							<div class="flex-1 p-2 border border-t-0 bg-white mr-1">Enable security teams to become focused on strategic value and added-value tasks and outputs. </div>
							<div class="flex-1 p-2 border border-t-0 bg-white">Empower your CISO or security lead, to become a strategic function. Support them in becoming a go-to source of strategic input to change and transformation initiatives.</div>
						</div>
					</div>					
				</div>
			</div>
			<div class="overflow-y-auto max-h-full" v-else-if="slide == 5">
				<div class="bg-capgemini-pink p-4 leading-none">
					<h2 class="text-lg font-bold">Think of &hellip;</h2>
				</div>
				<div class="p-4">
					<ol class="ml-4">
						<li class="list-decimal leading-tight rounded mb-2">Employing security by design and default as an organizational principle, not a formal "tick-box" exercise</li>
						<li class="list-decimal leading-tight rounded mb-2">Endorsing and advocating security change across the organization’s stakeholders to ensure change "stickiness"</li>
						<li class="list-decimal leading-tight rounded mb-2">Using security as a value-added component to your services and products to harness digital trust</li>
					</ol>
				</div>
				<div class="card">
					<div class="p-4">
						<h2 class="text-lg font-bold mb-4">Further info:</h2>
						<div class="flex">
							<div class="w-12 mr-2">
								<svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" viewBox="0 0 36 36">Read more icon<g fill="none" fill-rule="evenodd" stroke="#0070AD" stroke-width="2"><path class="border_svg" d="M1 18c0 9.39 7.61 17 17 17s17-7.61 17-17S27.39 1 18 1 1 8.61 1 18z"></path><path d="M15.018 11.997l3.958 3.958 2.042 2.042-6.021 6.02"></path></g></svg>
							</div>
							<div class="flex-1">
								<button type="button" @click="showCovid()">
									<p class="p-1 mb-2 font-bold">How does Covid-19 affect Security?</p>									
								</button>
							</div>
						</div>
						<div class="flex">
							<div class="w-12 mr-2">
								<svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" viewBox="0 0 36 36">Read more icon<g fill="none" fill-rule="evenodd" stroke="#0070AD" stroke-width="2"><path class="border_svg" d="M1 18c0 9.39 7.61 17 17 17s17-7.61 17-17S27.39 1 18 1 1 8.61 1 18z"></path><path d="M15.018 11.997l3.958 3.958 2.042 2.042-6.021 6.02"></path></g></svg>
							</div>
							<div class="flex-1">
								<a href="https://www.capgemini.com/wp-content/uploads/2019/01/The-Modern-Connected-CISO.pdf" target="_blank" @click="trackDownload">
									<p class="p-1 mb-2 font-bold">Download The Modern, Connected CISO InfoBrief</p>
									<div class="mb-2 p-2 border bg-white w-2/3">
										<img src="../assets/infobrief.png" alt="" class="">
									</div>
									
								</a>
							</div>
						</div>
						<div class="flex mt-4">
							<div class="w-12 mr-2">
								<svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" viewBox="0 0 36 36">Read more icon<g fill="none" fill-rule="evenodd" stroke="#0070AD" stroke-width="2"><path class="border_svg" d="M1 18c0 9.39 7.61 17 17 17s17-7.61 17-17S27.39 1 18 1 1 8.61 1 18z"></path><path d="M15.018 11.997l3.958 3.958 2.042 2.042-6.021 6.02"></path></g></svg>
							</div>
							<div class="flex-1">
								<a href="https://www.capgemini.com/cybersecurity" target="_blank" @click="trackVisit">
									<p class="p-1 font-bold">Find out more about Cybersecurity Services at capgemini.com</p>									
								</a>
							</div>
						</div>						
					</div>
				</div>
			</div>
		</transition>
		<slot name="control"></slot>
	</div>
</template>

<script>
	export default {

		name: 'CeoSlide',
		props: ['slide', 'total'],
		data () {
			return {

			}
		},
		methods:{
			trackDownload: function(){
				this.$gtag.event('download', {
					'event_category': 'PDF',
					'event_label': 'Infobrief'
				})
			},
			trackVisit: function(){
				this.$gtag.event('click', {
					'event_category': 'Link',
					'event_label': 'https://www.capgemini.com/cybersecurity'
				})
			},
			showCovid: function(){
				this.$emit('showCovid');
			}
		}
	}
</script>

<style lang="css" scoped>
.fade-enter-active, .fade-leave-active {
	transition: opacity .3s ease;
}
.fade-enter, .fade-leave-to
/* .component-fade-leave-active below version 2.1.8 */ {
	opacity: 0;
}
.card{
	@apply bg-gray-200;
	/*background-image: url(~@/assets/invent-card.png); background-size: contain; background-repeat: no-repeat; background-position: bottom right*/
}
</style>